import { useState, useCallback, useRef, type ReactNode } from 'react';
import { useTranslation } from 'react-i18next';
import { Info, X } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../../../components/ui/tooltip';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '../../../components/ui/popover';
import { cn } from '../../../lib/utils';
import { useResponsive } from '../../hooks/useResponsive';

interface InfoTooltipProps {
  /** The tooltip content to display. Usually a translated string; accepts a node
   *  so a sentence can carry an isolated token (e.g. a `<Trans>` with an
   *  `<LtrToken>` around an ID), which a plain string cannot express. */
  content: ReactNode;
  /** Additional className for the icon */
  className?: string;
  /** Icon size - defaults to 3.5 (h-3.5 w-3.5) */
  size?: 3 | 3.5 | 4 | 5;
  /** Side of the tooltip - defaults to 'right' for better form layout */
  side?: 'top' | 'right' | 'bottom' | 'left';
  /** Accessible label for the trigger button (defaults to "More information"). */
  ariaLabel?: string;
}

/**
 * A small info icon that displays contextual help.
 *
 * - **Desktop**: Tooltip on hover; click pins it open as a popover
 *   (close via X button or clicking outside)
 * - **Mobile/touch**: Popover on tap (since hover isn't available)
 *
 * Use this next to section titles, form labels, or anywhere brief
 * explanatory text helps the user understand a control or concept.
 */
export function InfoTooltip({
  content,
  className,
  size = 3.5,
  side = 'right',
  ariaLabel,
}: InfoTooltipProps) {
  const { t } = useTranslation('common');
  const { isMobile } = useResponsive();
  const sizeClass = size === 3.5 ? 'h-3.5 w-3.5' : `h-${size} w-${size}`;

  const [pinned, setPinned] = useState(false);
  const [tooltipOpen, setTooltipOpen] = useState(false);
  // Suppress tooltip flash when closing popover
  const suppressUntilLeaveRef = useRef(false);

  const handleClick = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setPinned(true);
    setTooltipOpen(false);
  }, []);

  const handleClose = useCallback(() => {
    setPinned(false);
    suppressUntilLeaveRef.current = true;
  }, []);

  const handleTooltipOpenChange = useCallback((open: boolean) => {
    if (pinned || suppressUntilLeaveRef.current) {
      setTooltipOpen(false);
      return;
    }
    setTooltipOpen(open);
  }, [pinned]);

  const icon = (
    <button
      type="button"
      aria-label={ariaLabel ?? t('app.moreInformation')}
      className={cn(
        'inline-flex items-center justify-center cursor-help bg-transparent border-0 p-0 text-inherit focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 rounded-sm',
        className
      )}
      onClick={!isMobile ? handleClick : undefined}
      onPointerLeave={!isMobile ? () => { suppressUntilLeaveRef.current = false; } : undefined}
    >
      <Info className={cn(sizeClass, 'text-muted-foreground')} />
    </button>
  );

  if (isMobile) {
    return (
      <Popover>
        <PopoverTrigger asChild>{icon}</PopoverTrigger>
        <PopoverContent
          side={side}
          collisionPadding={16}
          className="max-w-[280px] p-3 text-xs"
        >
          <p dir="auto">{content}</p>
        </PopoverContent>
      </Popover>
    );
  }

  // Desktop: hover tooltip + click-to-pin popover
  return (
    <TooltipProvider>
      <Popover open={pinned} onOpenChange={(open) => { if (!open) handleClose(); }}>
        <Tooltip delayDuration={300} open={tooltipOpen} onOpenChange={handleTooltipOpenChange}>
          <PopoverTrigger asChild>
            <TooltipTrigger asChild>{icon}</TooltipTrigger>
          </PopoverTrigger>
          <TooltipContent
            side={side}
            collisionPadding={16}
            className="max-w-[280px] text-wrap text-xs"
          >
            <p dir="auto">{content}</p>
          </TooltipContent>
        </Tooltip>
        {pinned && (
          <PopoverContent
            side={side}
            collisionPadding={16}
            className="max-w-[280px] p-3 text-xs relative"
            onOpenAutoFocus={(e) => e.preventDefault()}
          >
            <button
              type="button"
              onClick={handleClose}
              className="absolute top-1.5 end-1.5 p-0.5 rounded-sm hover:bg-accent text-muted-foreground hover:text-foreground transition-colors"
              aria-label={t('app.close')}
            >
              <X className="h-3 w-3" />
            </button>
            <p dir="auto" className="pe-4">{content}</p>
          </PopoverContent>
        )}
      </Popover>
    </TooltipProvider>
  );
}
